import {
  ConfirmationDialog,
  DataGrid,
  Dialog,
  Form,
  formatDate,
  FormButton,
  Input,
  SelectInput,
  StatusBadge,
  useNotifications
} from '@ijoin/components';
import {
  RkoSsoItem,
  useDeleteAdminRecordKeepingOrganizationSso,
  useGetAdminAccountTypes,
  useGetAdminRecordKeepingOrganizationSsos,
  usePatchAdminRecordKeepingOrganizationSso,
  usePostAdminRecordKeepingOrganizationSso
} from '@ijoin/hooks';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';

const EXPIRING_SOON_DAYS = 30;

type SsoFormProps = {
  accountTypeId: string;
  advisorCertificate: string;
  certificate: string;
  expiration: string;
  privateKey: string;
};

const EMPTY_FORM: SsoFormProps = {
  accountTypeId: '',
  advisorCertificate: '',
  certificate: '',
  expiration: '',
  privateKey: ''
};

const EXPIRATION_LABELS = {
  error: 'Expired',
  success: 'Valid',
  warning: 'Expires Soon'
};

const expirationSeverity = (expiration: string) => {
  const expires = dayjs(expiration);

  if (expires.isBefore(dayjs())) return 'error';

  return expires.isBefore(dayjs().add(EXPIRING_SOON_DAYS, 'day'))
    ? 'warning'
    : 'success';
};

export const RkoSsoSection: FC<{
  recordKeepingOrganizationId: string;
}> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<null | RkoSsoItem>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<null | RkoSsoItem>(null);

  const ssos = useGetAdminRecordKeepingOrganizationSsos(
    props.recordKeepingOrganizationId
  );
  const accountTypes = useGetAdminAccountTypes();

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [
        `/api/v3/admin/record-keeping-organizations/${props.recordKeepingOrganizationId}/ssos`
      ]
    });

  const createSso = usePostAdminRecordKeepingOrganizationSso({
    mutation: {
      onError: () => notifications.error('Failed to add SSO entry'),
      onSuccess: () => {
        invalidate();
        notifications.success('SSO entry added');
        onCloseDialog();
      }
    }
  });

  const updateSso = usePatchAdminRecordKeepingOrganizationSso({
    mutation: {
      onError: () => notifications.error('Failed to update SSO entry'),
      onSuccess: () => {
        invalidate();
        notifications.success('SSO entry updated');
        onCloseDialog();
      }
    }
  });

  const deleteSso = useDeleteAdminRecordKeepingOrganizationSso({
    mutation: {
      onError: () => notifications.error('Failed to remove SSO entry'),
      onSuccess: () => {
        invalidate();
        notifications.success('SSO entry removed');
      }
    }
  });

  const methods = useForm<SsoFormProps>({ defaultValues: EMPTY_FORM });

  const onOpenAdd = () => {
    setEditing(null);
    methods.reset(EMPTY_FORM);
    setIsDialogOpen(true);
  };

  const onOpenEdit = (sso: RkoSsoItem) => {
    setEditing(sso);
    methods.reset({
      accountTypeId: sso.accountTypeId ?? '',
      advisorCertificate: sso.advisorCertificate ?? '',
      certificate: sso.certificate,
      expiration: '',
      privateKey: ''
    });
    setIsDialogOpen(true);
  };

  const onCloseDialog = () => {
    setIsDialogOpen(false);
    setEditing(null);
  };

  const onSubmit = (values: SsoFormProps) => {
    const data = {
      accountTypeId: values.accountTypeId || null,
      advisorCertificate: values.advisorCertificate || null,
      certificate: values.certificate,
      expiration: values.expiration || undefined,
      privateKey: values.privateKey || undefined
    };

    if (editing) {
      updateSso.mutate({
        data,
        recordKeepingOrganizationId: props.recordKeepingOrganizationId,
        ssoId: editing.ssoId
      });
    } else {
      createSso.mutate({
        data,
        recordKeepingOrganizationId: props.recordKeepingOrganizationId
      });
    }
  };

  const onCancelDelete = () => setDeleting(null);

  const onConfirmDelete = () => {
    if (deleting) {
      deleteSso.mutate({
        recordKeepingOrganizationId: props.recordKeepingOrganizationId,
        ssoId: deleting.ssoId
      });
    }
    setDeleting(null);
  };

  const columns: GridColDef<RkoSsoItem>[] = [
    {
      field: 'accountTypeDescription',
      flex: 1,
      headerName: 'Account Type',
      valueGetter: value => value ?? ''
    },
    {
      field: 'expiration',
      flex: 1.5,
      headerName: 'Expiration',
      renderCell: params => {
        const severity = expirationSeverity(params.row.expiration);

        return (
          <Stack
            alignItems='center'
            direction='row'
            spacing={1}
            sx={{ height: '100%' }}>
            <span>
              {formatDate(params.row.expiration, { format: 'MM/DD/YYYY' })}
            </span>
            <StatusBadge severity={severity}>
              {EXPIRATION_LABELS[severity]}
            </StatusBadge>
          </Stack>
        );
      }
    },
    {
      field: 'hasPrivateKey',
      flex: 1,
      headerName: 'Private Key',
      renderCell: params => (
        <StatusBadge severity={params.row.hasPrivateKey ? 'success' : 'info'}>
          {params.row.hasPrivateKey ? 'Present' : 'None'}
        </StatusBadge>
      )
    },
    {
      field: 'advisorCertificate',
      flex: 1,
      headerName: 'Advisor Certificate',
      renderCell: params => (
        <StatusBadge
          severity={params.row.advisorCertificate ? 'success' : 'info'}>
          {params.row.advisorCertificate ? 'Present' : 'None'}
        </StatusBadge>
      )
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <>
          <Tooltip title='Edit'>
            <IconButton
              data-testid={`editSso-${params.row.ssoId}`}
              onClick={() => onOpenEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Remove'>
            <IconButton
              data-testid={`deleteSso-${params.row.ssoId}`}
              onClick={() => setDeleting(params.row)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
      sortable: false,
      width: 110
    }
  ];

  return (
    <Stack spacing={1}>
      <Stack alignItems='center' direction='row' spacing={1}>
        <Typography variant='h6'>SSO Certificates</Typography>
        <Tooltip title='Add SSO entry'>
          <IconButton data-testid='addSso' onClick={onOpenAdd}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <DataGrid
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.ssoId}
        loading={ssos.isFetching}
        rows={ssos.data ?? []}
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'ssoCertificates'
          }
        }}
      />

      <Dialog
        data-testid='ssoDialog'
        fullWidth
        isOpen={isDialogOpen}
        maxWidth='md'
        onClose={onCloseDialog}>
        <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle>
            {editing ? 'Edit SSO Entry' : 'Add SSO Entry'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <SelectInput
                fullWidth
                hideError
                label='Account Type'
                name='accountTypeId'
                options={(accountTypes.data ?? []).map(type => ({
                  label: type.description,
                  value: type.accountTypeId
                }))}
                placeholder='Select an account type'
                size='small'
                variant='outlined'
              />
              <Input
                data-testid='ssoCertificate'
                format='string'
                fullWidth
                hideError
                label='Certificate (PEM)'
                multiline
                name='certificate'
                rows={5}
                size='small'
              />
              <Input
                data-testid='ssoAdvisorCertificate'
                format='string'
                fullWidth
                hideError
                label='Advisor Certificate (PEM)'
                multiline
                name='advisorCertificate'
                rows={5}
                size='small'
              />
              <Input
                data-testid='ssoPrivateKey'
                format='string'
                fullWidth
                hideError
                label={
                  editing
                    ? 'Private Key (leave blank to keep the current key)'
                    : 'Private Key'
                }
                multiline
                name='privateKey'
                rows={5}
                size='small'
              />
              <Input
                data-testid='ssoExpiration'
                format='date'
                fullWidth
                hideError
                label='Expiration (only used when the certificate cannot be parsed)'
                name='expiration'
                size='small'
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelSso'
              onClick={onCloseDialog}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='submitSso'
              disabled={createSso.isPending || updateSso.isPending}
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>

      <ConfirmationDialog
        confirmColor='error'
        confirmText='Remove'
        data-testid='deleteSso'
        isOpen={!!deleting}
        message='Remove this SSO entry? Record keeper SSO launches that rely on this certificate will stop working.'
        onCancel={onCancelDelete}
        onConfirm={onConfirmDelete}
        title='Remove SSO Entry'
      />
    </Stack>
  );
};

RkoSsoSection.displayName = 'RkoSsoSection';
